import SeoHead from "@/Component/Seo/SeoHead";
import BlogDestination from "@/Component/BlogDestination";
import SingleBlogDetail from "@/Component/SingleBlogDetail";
import {
  serverFetchBlogCategories,
  serverFetchBlogDetailBySlug,
  serverFetchBlogsByCategorySlug,
  serverFetchRecentBlogs,
} from "@/services/server";
import { getCountryFromContext } from "@/utils/getCountryFromRequest";
import {
  findBlogCategory,
  isBlogCategorySlug,
  normalizeBlogCategoriesResponse,
  normalizeBlogDetailResponse,
  normalizeCategoryBlogsResponse,
  normalizeRecentBlogsResponse,
} from "@/utils/blogHelpers";
import { renderNotFoundPage } from "@/utils/detailSlug";
import { fetchPageSeo } from "@/utils/pageSeo";

const RECENT_BLOGS_COUNT = 5;

export default function DynamicBlogPage({
  seo,
  seoCountry,
  slug,
  isCategory,
  categoryInfo,
  initialCategoryData,
  blogDetail,
  recentBlogs,
  country,
}) {
  const fallbackTitle = isCategory
    ? categoryInfo?.title
      ? `${categoryInfo.title} | Next Holidays`
      : "Blog Category | Next Holidays"
    : blogDetail?.title
      ? `${blogDetail.title} | Next Holidays`
      : "Blog | Next Holidays";

  if (isCategory) {
    return (
      <>
        <SeoHead seo={seo} country={seoCountry} fallbackTitle={fallbackTitle} />
        <BlogDestination
          categorySlug={slug}
          categoryInfo={categoryInfo}
          initialCategoryData={initialCategoryData}
          country={country}
        />
      </>
    );
  }

  return (
    <>
      <SeoHead seo={seo} country={seoCountry} fallbackTitle={fallbackTitle} />
      <SingleBlogDetail
        postSlug={slug}
        blogDetail={blogDetail}
        recentBlogs={recentBlogs}
      />
    </>
  );
}

export async function getServerSideProps(context) {
  const slugParam = context.params?.slug;
  let slug = Array.isArray(slugParam) ? slugParam[0] : slugParam;
  slug = String(slug || "").trim();

  if (!slug) {
    return renderNotFoundPage(context);
  }

  if (slug.toLowerCase() === "author") {
    return renderNotFoundPage(context);
  }

  if (slug.toLowerCase() === "destinations") {
    const country = getCountryFromContext(context);
    const prefix = country === "IN" ? "/in" : "";
    return {
      redirect: {
        destination: `${prefix}/blog/destination`,
        permanent: true,
      },
    };
  }

  const country = getCountryFromContext(context);
  const seoResult = await fetchPageSeo(context);
  if (seoResult.redirect) return seoResult;

  try {
    const categoriesResponse = await serverFetchBlogCategories(country);
    const categories = normalizeBlogCategoriesResponse(categoriesResponse);
    const isCategory = isBlogCategorySlug(slug, categories);

    if (isCategory) {
      const categoryResponse = await serverFetchBlogsByCategorySlug(slug, country);
      const initialCategoryData = normalizeCategoryBlogsResponse(
        categoryResponse,
        slug
      );
      const categoryInfo = findBlogCategory(slug, categories);

      return {
        props: {
          seo: seoResult.seo ?? null,
          seoCountry: seoResult.seoCountry ?? null,
          slug,
          isCategory: true,
          categoryInfo: categoryInfo || null,
          initialCategoryData,
          blogDetail: null,
          recentBlogs: [],
          country,
        },
      };
    }
  } catch (error) {
    console.error("Blog category SSR error:", error);
  }

  try {
    const [detailResponse, recentResponse] = await Promise.all([
      serverFetchBlogDetailBySlug(slug, country),
      serverFetchRecentBlogs(RECENT_BLOGS_COUNT, country),
    ]);

    const blogDetail = normalizeBlogDetailResponse(detailResponse, slug);
    const recentBlogs = normalizeRecentBlogsResponse(recentResponse, {
      excludeSlug: slug,
    });

    if (!blogDetail?.title) {
      return renderNotFoundPage(context);
    }

    return {
      props: {
        seo: seoResult.seo ?? null,
        seoCountry: seoResult.seoCountry ?? null,
        slug,
        isCategory: false,
        categoryInfo: null,
        initialCategoryData: null,
        blogDetail,
        recentBlogs,
        country,
      },
    };
  } catch (error) {
    console.error("Blog detail SSR error:", error);
    return renderNotFoundPage(context);
  }
}
